import styles from './GestureMap.module.css'
import { AcrossDisplays } from './AcrossDisplays'

/** Where a four-finger swipe can send a window. All are assignable actions. */
const POSITIONS_ARIA =
  'Window position map. Gesturely can place a window in the left half, right half, ' +
  'top left, top right, bottom left or bottom right of the screen, and can maximize ' +
  'or minimize it. Any of these can be assigned to a gesture.'

/** The five placement mappings Gesturely ships preconfigured. */
const DEFAULT_GESTURES = [
  { gesture: 'Four-finger swipe left', target: 'Left Half' },
  { gesture: 'Four-finger swipe right', target: 'Right Half' },
  { gesture: 'Four-finger swipe up', target: 'Maximize' },
  { gesture: 'Four-finger swipe down', target: 'Minimize' },
  { gesture: 'Three-finger double-tap', target: 'Full Screen' },
]

/** The two display-move defaults. Separate because they leave the display. */
const DISPLAY_GESTURES = [
  { gesture: 'Shift + four-finger swipe left', target: 'Left display' },
  { gesture: 'Shift + four-finger swipe right', target: 'Right display' },
]

/**
 * Three separated ideas: the positions a window can be sent to, the gestures
 * that ship already mapped, and the two that move a window between displays.
 *
 * Only the compass is a diagram, so only it carries `role="img"`. The two
 * mapping lists are real text and are read normally, which keeps the factual
 * default mappings available to assistive tech rather than buried in a label.
 */
export function GestureMap() {
  return (
    <div className={styles.map}>
      {/* 1 ── Window positions: capability, not defaults. */}
      <section className={styles.group}>
        <p className={styles.kicker}>Window positions</p>
        <p className={styles.groupNote}>Place a window exactly where you want it.</p>

        <div className={styles.diagram} role="img" aria-label={POSITIONS_ARIA}>
          <div className={`${styles.corner} ${styles.topLeft}`}>
            <span className={styles.cornerLabel}>Top Left</span>
            <span className={styles.diagonal} />
          </div>

          <div className={`${styles.arm} ${styles.up}`}>
            <span className={styles.label}>Maximize</span>
            <span className={styles.line} />
          </div>

          <div className={`${styles.corner} ${styles.topRight}`}>
            <span className={styles.diagonal} />
            <span className={styles.cornerLabel}>Top Right</span>
          </div>

          <div className={`${styles.arm} ${styles.left}`}>
            <span className={styles.label}>Left Half</span>
            <span className={styles.line} />
          </div>

          <div className={styles.plate}>
            <span className={styles.dot} />
          </div>

          <div className={`${styles.arm} ${styles.right}`}>
            <span className={styles.line} />
            <span className={styles.label}>Right Half</span>
          </div>

          <div className={`${styles.corner} ${styles.bottomLeft}`}>
            <span className={styles.cornerLabel}>Bottom Left</span>
            <span className={styles.diagonal} />
          </div>

          <div className={`${styles.arm} ${styles.down}`}>
            <span className={styles.line} />
            <span className={styles.label}>Minimize</span>
          </div>

          <div className={`${styles.corner} ${styles.bottomRight}`}>
            <span className={styles.diagonal} />
            <span className={styles.cornerLabel}>Bottom Right</span>
          </div>
        </div>
      </section>

      {/* 2 ── The placement gestures that are already mapped. */}
      <section className={`${styles.group} ${styles.divided}`}>
        <p className={styles.kicker}>Ready from the start</p>
        <ul className={styles.mappings}>
          {DEFAULT_GESTURES.map((item) => (
            <li key={item.gesture} className={styles.mappingRow}>
              <span className={styles.rowGesture}>{item.gesture}</span>
              <span className={styles.rowLine} aria-hidden="true" />
              <span className={styles.rowTarget}>{item.target}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* 3 ── Moving a window to another display, not another position. */}
      <section className={`${styles.group} ${styles.divided}`}>
        <p className={styles.kicker}>Across displays</p>
        <AcrossDisplays />
        <ul className={styles.mappings}>
          {DISPLAY_GESTURES.map((item) => (
            <li key={item.gesture} className={styles.mappingRow}>
              <span className={styles.rowGesture}>{item.gesture}</span>
              <span className={styles.rowLine} aria-hidden="true" />
              <span className={styles.rowTarget}>{item.target}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}
